import {
  Box,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { InputRules } from '@/types/inputs';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import SVTextInput from '../../core/inputs/SVTextInput';

type SVSubdomainInputProps = {
  name: string;
  label?: string;
  hideLabel?: boolean;
  disabled?: boolean;
  rules?: InputRules;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  onValueChange?: (value: string) => void;
  externalValue?: string;
};

const validateSubdomainFormat = (value: string): string | true => {
  if (!value) return true; // Let required validation handle empty values

  // Check for uppercase letters
  if (/[A-Z]/.test(value)) {
    return 'Subdomain must contain only lowercase letters';
  }

  // Check for invalid special characters (only hyphens and underscores allowed)
  if (!/^[a-z0-9_-]+$/.test(value)) {
    return 'Subdomain can only contain lowercase letters, numbers, hyphens, and underscores';
  }

  // Check for consecutive hyphens or underscores
  if (/-{2,}/.test(value) || /_{2,}/.test(value)) {
    return 'Subdomain cannot contain consecutive hyphens or underscores';
  }

  // Check for leading/trailing hyphens or underscores
  if (/^[-_]|[-_]$/.test(value)) {
    return 'Subdomain cannot start or end with hyphens or underscores';
  }

  return true;
};

export default function SVSubdomainInput({
  name,
  label = 'Subdomain',
  hideLabel = false,
  disabled = false,
  rules,
  required = true,
  placeholder = 'Enter Subdomain',
  maxLength = 50,
  onValueChange,
  externalValue,
}: SVSubdomainInputProps) {
  const methods = useFormContext();
  const hasBeenManuallyEdited = useRef<boolean>(false);
  const lastExternalValue = useRef<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Watch the field value and form state for real-time updates
  const fieldValue = useWatch({ control: methods.control, name });
  const {
    formState: { errors, isSubmitted, touchedFields },
  } = methods;

  // Handle external value updates - auto-populate if field hasn't been touched or is empty
  useEffect(() => {
    // Check if external value was cleared (reset scenario)
    if (
      (!externalValue || externalValue.trim() === '') &&
      lastExternalValue.current.trim() !== ''
    ) {
      hasBeenManuallyEdited.current = false;
    }

    // Only update if the value is actually different to prevent infinite loops
    // Prevent auto-population if user has manually edited the field
    if (
      externalValue &&
      externalValue.trim() !== '' &&
      !hasBeenManuallyEdited.current &&
      externalValue !== fieldValue // Prevent setting the same value
    ) {
      methods.setValue(name, externalValue, { shouldValidate: true });
      onValueChange?.(externalValue);
    }

    // Update the last external value
    lastExternalValue.current = externalValue || '';
  }, [externalValue, name, methods, onValueChange, fieldValue]);

  // Track when the user manually touches the field
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Mark as manually edited when user types anything
    hasBeenManuallyEdited.current = true;

    // Return the event so the form can handle it normally
    return event;
  };

  const hasError = !!errors[name]?.message;

  // Also check if the field has been touched and has validation errors
  const isFieldTouched = touchedFields[name];
  const shouldShowError = hasError && (isSubmitted || isFieldTouched);

  // Create enhanced rules with built-in validation
  const enhancedRules: InputRules = {
    ...rules,
    validate: (value: string, formContext: any) => {
      // Check if value is required
      if (required && (!value || value.trim() === '')) {
        return 'Subdomain is required';
      }

      // Run subdomain format validation
      const subdomainValidation = validateSubdomainFormat(value);
      if (subdomainValidation !== true) {
        return subdomainValidation;
      }

      // Run custom validation if provided
      if (rules?.validate && typeof rules.validate === 'function') {
        const customValidation = rules.validate(value, formContext);
        if (customValidation !== true) {
          return customValidation;
        }
      }

      return true;
    },
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <Box sx={{ flex: 1 }}>
          <SVTextInput
            name={name}
            label={label}
            hideLabel={hideLabel}
            disabled={disabled}
            helperText={
              shouldShowError
                ? ''
                : 'Must be lowercase, only allow hyphens and underscores'
            }
            htmlInputProps={{
              placeholder,
            }}
            customOnChangeEvent={handleInputChange}
            inputProps={{
              startAdornment: !isMobile ? (
                <InputAdornment position="start">
                  <Typography sx={{ fontSize: '22px' }}>https://</Typography>
                </InputAdornment>
              ) : undefined,
              endAdornment: !isMobile ? (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: '22px' }}>
                    .1advisor.com
                  </Typography>
                </InputAdornment>
              ) : undefined,
              placeholder: isMobile ? 'Company Subdomain' : 'Company Subdomain',
              inputProps: {
                maxLength,
                style: {
                  paddingLeft: isMobile ? 16 : 0,
                  marginLeft: isMobile ? 0 : '-7px',
                  minWidth: isMobile ? '100%' : '120px',
                  flex: 1,
                },
              },
            }}
            rules={enhancedRules}
          />
          {isMobile && fieldValue && (
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: 'text.secondary',
                fontSize: '12px',
                fontFamily: 'monospace',
              }}>
              https://
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                {fieldValue}
              </Box>
              .1advisor.com
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
